import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Form, Container, Button, Toast, Spinner } from "react-bootstrap";
import styles from "./sponsorCroForm.module.css";
import { axiosPost } from "../../../UTILS/AXIOS";
import { sponsorApi } from "../../../CONSTANTS";
import FormSecurity from "../../FORM_SECURITY";
import {
  rules,
  securityFields,
  isSuccess,
  failureMessage,
  applyServerErrors,
} from "../../../UTILS/VALIDATION";

// Only website and message are optional.
const schema = Yup.object().shape({
  organizationName: rules.text("Organization/Company Name", 200),
  contactPersonName: rules.name("Primary Contact Person Name"),
  email: rules.email(),
  phone: rules.phone(),
  website: rules.url(),
  address: rules.text("Address", 200),
  city: rules.text("City", 100),
  state: rules.text("State", 100),
  country: rules.text("Country", 100),
  message: rules.text("Message", 2000, false),
  ...securityFields,
});

const fields = [
  { name: "organizationName", label: "Organization / Company Name", type: "text", required: true, autoComplete: "organization" },
  { name: "contactPersonName", label: "Primary Contact Person Name", type: "text", required: true, autoComplete: "name" },
  { name: "email", label: "Email Address", type: "email", required: true, autoComplete: "email" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel" },
  { name: "website", label: "Organization Website", type: "url", required: false, autoComplete: "url" },
  { name: "address", label: "Address", type: "text", required: true, autoComplete: "street-address" },
  { name: "city", label: "City", type: "text", required: true, autoComplete: "address-level2" },
  { name: "state", label: "State", type: "text", required: true, autoComplete: "address-level1" },
  { name: "country", label: "Country", type: "text", required: true, autoComplete: "country-name" },
  { name: "message", label: "Interest / Message", type: "textarea", required: false },
];

const SponsorCroForm = () => {
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const captcha = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await axiosPost(sponsorApi, { ...data, formType: "sponsorCROContact" });
    if (isSuccess(result)) {
      setToastMessage("Thank you. Our sponsor team will be in touch soon.");
      setToastVariant("success");
      reset();
    } else {
      applyServerErrors(result, setError, Object.keys(schema.fields));
      setToastMessage(failureMessage(result));
      setToastVariant("danger");
    }
    captcha.current?.reset();
    setShowToast(true);
    setLoading(false);
  };

  const renderField = ({ name, label, type, required, autoComplete }) => (
    <Form.Group controlId={`form${name}`} key={name} className="mb-3 w-100">
      <Form.Label className={styles.label}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </Form.Label>
      {type === "textarea" ? (
        <Form.Control
          as="textarea"
          rows={4}
          maxLength={2000}
          placeholder="How can we help you"
          {...register(name)}
          isInvalid={!!errors[name]}
          disabled={loading}
          className={styles.formControl}
        />
      ) : (
        <Form.Control
          type={type}
          {...register(name)}
          autoComplete={autoComplete}
          aria-required={required}
          isInvalid={!!errors[name]}
          disabled={loading}
          className={styles.formControl}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      )}
      <Form.Control.Feedback type="invalid">{errors[name]?.message}</Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Container className={`${styles.sponsorCroFormContainer} position-relative`}>
      {loading && (
        <div className={styles.overlay}>
          <Spinner animation="border" variant="light" />
        </div>
      )}

      <Form noValidate onSubmit={handleSubmit(onSubmit)} className={styles.formSection}>
        {fields.map(renderField)}

        <FormSecurity ref={captcha} register={register} setValue={setValue} error={errors.recaptchaToken} theme="dark" />

        <div className="d-flex justify-content-center mt-4 w-100">
          <Button type="submit" disabled={loading} className={styles.submitButton}>
            Submit
          </Button>
        </div>
      </Form>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
        role="status"
        className={`bg-${toastVariant} text-white position-fixed`}
        style={{ bottom: "10px", right: "10px", zIndex: 10000 }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default SponsorCroForm;
